/**
 * SecureEmbed Connections Explorer
 *
 * Displays cross-domain entity relationships as a force-directed network graph
 * (Cytoscape.js) with an optional grouped card list below. Nodes represent
 * records from different datasources; edges represent semantic similarity,
 * correlation, or cross-domain links.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Loader2, Sparkles, ChevronDown, ChevronRight, ArrowRight,
  Link2, AlertCircle, LayoutGrid, Network, Plus,
} from 'lucide-react';
import { connectionsApi, ConnectionGroup, ConnectionNode, ConnectionsResponse } from '../services/api';
import DataSourceBadge from './DataSourceBadge';
import ConnectionsGraph, {
  AnalysisGraphNode, AnalysisGraphEdge, AnalysisNodeType, AnalysisEdgeType, LayoutType,
} from './ConnectionsGraph';

interface ConnectionsExplorerProps {
  query: string;
  onSearchFromHere: (term: string) => void;
}

// Map source/datasource string to graph node type
const mapSourceToNodeType = (source: string): AnalysisNodeType => {
  const map: Record<string, AnalysisNodeType> = {
    aemo_ingestion: 'aemo', aemo: 'aemo',
    'aemo-duid': 'aemo-duid', aemo_duid_ingestion: 'aemo-duid',
    'aemo-fcas': 'aemo-fcas', aemo_fcas_ingestion: 'aemo-fcas',
    bom_ingestion: 'bom', bom: 'bom',
    aer_ingestion: 'aer', aer: 'aer',
    regulatory_ingestion: 'regulatory', regulatory: 'regulatory',
    scholar_ingestion: 'scholarly', scholarly: 'scholarly',
    partner_ingestion: 'partner', partner: 'partner',
    'quantum-ecosystem': 'partner',
  };
  return map[source] || 'aemo';
};

// Map edge relationship text to graph edge type
const mapRelationshipToEdgeType = (rel: string): AnalysisEdgeType => {
  if (rel.includes('region')) return 'cross-domain';
  if (rel.includes('similar') || rel.includes('type')) return 'similarity';
  if (rel.includes('correlat')) return 'correlation';
  if (rel.includes('reference') || rel.includes('cite')) return 'reference';
  return 'cross-domain';
};

const LAYOUT_OPTIONS: { key: LayoutType; label: string }[] = [
  { key: 'cose', label: 'Force' },
  { key: 'circle', label: 'Circle' },
  { key: 'breadthfirst', label: 'Tree' },
  { key: 'grid', label: 'Grid' },
];

/** Number of new nodes to fetch when clicking a node to expand */
const DISCOVER_LIMIT = 10;

/** Node types that have embeddings and can be expanded on click */
const EXPANDABLE_TYPES = new Set<string>([
  'aemo', 'aemo-duid', 'aemo-fcas', 'bom', 'aer', 'regulatory', 'scholarly',
]);

const ConnectionsExplorer: React.FC<ConnectionsExplorerProps> = ({ query, onSearchFromHere }) => {
  const [data, setData] = useState<ConnectionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [graphExpandedNodes, setGraphExpandedNodes] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'graph' | 'list'>('graph');
  const [graphLayout, setGraphLayout] = useState<LayoutType>('cose');
  const [selectedGraphNode, setSelectedGraphNode] = useState<string | undefined>(undefined);
  const [additionalData, setAdditionalData] = useState<ConnectionsResponse | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [findingRelated, setFindingRelated] = useState(false);
  const [expanding, setExpanding] = useState(false);

  const explore = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setAdditionalData(null);
    setGraphExpandedNodes(new Set());
    try {
      const resp = await connectionsApi.explore(query, 30);
      setData(resp);
    } catch (err: any) {
      setError(err.message || 'Failed to explore connections');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    if (query.trim()) explore();
  }, [query, explore]);

  // Convert API response to graph-compatible format, merging additional data
  const graphNodes: AnalysisGraphNode[] = useMemo(() => {
    if (!data) return [];
    const baseNodes = data.groups.flatMap(group =>
      group.nodes.map(node => ({
        id: node.id,
        label: node.label,
        type: mapSourceToNodeType(node.source),
        score: node.score,
        content: node.content,
        timestamp: node.timestamp,
        region: node.region,
        crossSourceCount: node.crossSourceCount,
      }))
    );
    if (!additionalData) return baseNodes;
    const existingIds = new Set(baseNodes.map(n => n.id));
    const extraNodes = additionalData.groups.flatMap(group =>
      group.nodes.filter(n => !existingIds.has(n.id)).map(node => ({
        id: node.id,
        label: node.label,
        type: mapSourceToNodeType(node.source),
        score: node.score,
        content: node.content,
        timestamp: node.timestamp,
        region: node.region,
        crossSourceCount: node.crossSourceCount,
      }))
    );
    return [...baseNodes, ...extraNodes];
  }, [data, additionalData]);

  const graphEdges: AnalysisGraphEdge[] = useMemo(() => {
    if (!data) return [];
    const baseEdges = data.edges.map((edge, i) => ({
      id: `edge-${i}`,
      source: edge.from,
      target: edge.to,
      type: mapRelationshipToEdgeType(edge.relationship),
      weight: edge.weight,
      label: edge.relationship,
    }));
    if (!additionalData) return baseEdges;
    const extraEdges = additionalData.edges.map((edge, i) => ({
      id: `extra-edge-${i}`,
      source: edge.from,
      target: edge.to,
      type: mapRelationshipToEdgeType(edge.relationship),
      weight: edge.weight,
      label: edge.relationship,
    }));
    return [...baseEdges, ...extraEdges];
  }, [data, additionalData]);

  const discoveredCount = additionalData?.totalNodes || 0;

  const handleDiscoverMore = useCallback(async () => {
    if (!query.trim() || discovering) return;
    setDiscovering(true);
    try {
      const existingIds = graphNodes.map(n => n.id);
      const resp = await connectionsApi.discoverMore(existingIds, query, 25);
      setAdditionalData(prev => {
        if (!prev) return resp;
        // Merge new groups into existing additional data
        const merged: ConnectionsResponse = {
          groups: [...prev.groups],
          edges: [...prev.edges, ...resp.edges],
          totalNodes: prev.totalNodes + resp.totalNodes,
          totalEdges: prev.totalEdges + resp.totalEdges,
          queryTimeMs: resp.queryTimeMs,
        };
        for (const newGroup of resp.groups) {
          const existing = merged.groups.find(g => g.source === newGroup.source);
          if (existing) {
            const ids = new Set(existing.nodes.map(n => n.id));
            existing.nodes.push(...newGroup.nodes.filter(n => !ids.has(n.id)));
          } else {
            merged.groups.push(newGroup);
          }
        }
        return merged;
      });
    } catch {
      // Silently fail — user can retry
    } finally {
      setDiscovering(false);
    }
  }, [query, discovering, graphNodes]);

  const handleFindRelated = useCallback(async (node: AnalysisGraphNode) => {
    if (findingRelated) return;
    setFindingRelated(true);
    try {
      const existingIds = graphNodes.map(n => n.id);
      const resp = await connectionsApi.discoverMore(existingIds, node.label, 25);
      setAdditionalData(prev => {
        if (!prev) return resp;
        const merged: ConnectionsResponse = {
          groups: [...prev.groups],
          edges: [...prev.edges, ...resp.edges],
          totalNodes: prev.totalNodes + resp.totalNodes,
          totalEdges: prev.totalEdges + resp.totalEdges,
          queryTimeMs: resp.queryTimeMs,
        };
        for (const newGroup of resp.groups) {
          const existing = merged.groups.find(g => g.source === newGroup.source);
          if (existing) {
            const ids = new Set(existing.nodes.map(n => n.id));
            existing.nodes.push(...newGroup.nodes.filter(n => !ids.has(n.id)));
          } else {
            merged.groups.push(newGroup);
          }
        }
        return merged;
      });
    } catch {
      // Silently fail
    } finally {
      setFindingRelated(false);
    }
  }, [findingRelated, graphNodes]);

  const handleExpandNode = useCallback(async (nodeId: string, nodeType: string) => {
    if (graphExpandedNodes.has(nodeId)) return;
    if (!EXPANDABLE_TYPES.has(nodeType)) return;
    if (expanding) return;

    setGraphExpandedNodes(prev => new Set(prev).add(nodeId));
    setExpanding(true);
    try {
      const existingIds = graphNodes.map(n => n.id);
      const resp = await connectionsApi.discoverMore(existingIds, query, DISCOVER_LIMIT);
      setAdditionalData(prev => {
        if (!prev) return resp;
        const merged: ConnectionsResponse = {
          groups: [...prev.groups],
          edges: [...prev.edges, ...resp.edges],
          totalNodes: prev.totalNodes + resp.totalNodes,
          totalEdges: prev.totalEdges + resp.totalEdges,
          queryTimeMs: resp.queryTimeMs,
        };
        for (const newGroup of resp.groups) {
          const existing = merged.groups.find(g => g.source === newGroup.source);
          if (existing) {
            const ids = new Set(existing.nodes.map(n => n.id));
            existing.nodes.push(...newGroup.nodes.filter(n => !ids.has(n.id)));
          } else {
            merged.groups.push(newGroup);
          }
        }
        return merged;
      });
    } catch {
      // Remove from expanded set so user can retry
      setGraphExpandedNodes(prev => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
    } finally {
      setExpanding(false);
    }
  }, [graphExpandedNodes, expanding, graphNodes, query]);

  const handleOpenSource = useCallback((node: AnalysisGraphNode) => {
    const label = encodeURIComponent(node.label);
    switch (node.type) {
      case 'scholarly':
        window.open(`https://scholar.google.com/scholar?q=${label}`, '_blank');
        break;
      case 'partner':
        // Navigate to partners page on the portal
        window.location.href = '/partners';
        break;
      case 'aemo':
      case 'aemo-duid':
      case 'aemo-fcas':
      case 'bom':
      case 'aer':
        onSearchFromHere(node.label);
        break;
      case 'regulatory':
        onSearchFromHere(node.label);
        break;
      default:
        onSearchFromHere(node.label);
    }
  }, [onSearchFromHere]);

  const toggleExpand = (nodeId: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const getScoreBg = (score: number): string => {
    if (score >= 0.9) return 'bg-green-100 text-green-700';
    if (score >= 0.7) return 'bg-blue-100 text-blue-700';
    if (score >= 0.5) return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-600';
  };

  const handleGraphNodeClick = (node: AnalysisGraphNode) => {
    setSelectedGraphNode(node.id);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
        <Loader2 className="w-8 h-8 text-[#2B064F] animate-spin mx-auto mb-4" />
        <p className="text-gray-500">Exploring connections...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  if (!data || !query.trim()) {
    return (
      <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
        <Link2 className="w-12 h-12 text-purple-300 mx-auto mb-4" />
        <p className="text-gray-700 font-medium">Run a search to explore connections</p>
        <p className="text-gray-500 text-sm mt-1">Cross-domain entity relationships will appear here</p>
      </div>
    );
  }

  if (data.totalNodes === 0) {
    return (
      <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
        <Link2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No connections found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar: summary + view toggle + layout selector */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span>{graphNodes.length} entities across {data.groups.length}{additionalData ? '+' : ''} sources</span>
          <span className="flex items-center gap-1">
            <Sparkles className="w-4 h-4 text-purple-500" />
            {graphEdges.length} cross-source connections
            <span className="ml-2 text-gray-400">{data.queryTimeMs}ms</span>
          </span>
          {discoveredCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
              +{discoveredCount} discovered
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Discover more button */}
          <button
            onClick={handleDiscoverMore}
            disabled={discovering}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors disabled:opacity-50"
          >
            {discovering ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
            {discovering ? 'Discovering...' : 'Discover 25 More'}
          </button>

          {/* Layout selector (visible in graph mode) */}
          {viewMode === 'graph' && (
            <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
              {LAYOUT_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setGraphLayout(opt.key)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                    graphLayout === opt.key
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {/* View toggle */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('graph')}
              className={`p-1.5 rounded-md transition-colors ${
                viewMode === 'graph' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Network graph view"
            >
              <Network className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md transition-colors ${
                viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Card list view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Network Graph View */}
      {viewMode === 'graph' && (
        <>
          <ConnectionsGraph
            nodes={graphNodes}
            edges={graphEdges}
            layout={graphLayout}
            selectedNodeId={selectedGraphNode}
            onNodeClick={handleGraphNodeClick}
            onOpenSource={handleOpenSource}
            onFindRelated={handleFindRelated}
            onExpandNode={handleExpandNode}
            expandedNodeIds={graphExpandedNodes}
            findingRelated={findingRelated}
            expanding={expanding}
            className="w-full"
          />
          <div className="flex items-center justify-center">
            <button
              onClick={handleDiscoverMore}
              disabled={discovering}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors disabled:opacity-50"
            >
              {discovering ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              {discovering ? 'Discovering...' : `Find ${DISCOVER_LIMIT} More`}
            </button>
          </div>
        </>
      )}

      {/* Card List View */}
      {viewMode === 'list' && (
        <div className="space-y-4">
          {data.groups.map((group: ConnectionGroup) => (
            <div key={group.source} className="space-y-2">
              {/* Group header */}
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color }} />
                <DataSourceBadge source={group.source} />
                <span className="text-xs text-gray-400">{group.nodes.length} entities</span>
              </div>

              {/* Nodes */}
              {group.nodes.map((node: ConnectionNode) => {
                const expanded = expandedCards.has(node.id);
                return (
                  <div
                    key={node.id}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                    style={{ borderLeftWidth: 4, borderLeftColor: group.color }}
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          {/* Score badge */}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${getScoreBg(node.score)}`}>
                            {(node.score * 100).toFixed(0)}%
                          </span>
                          {/* Type */}
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {node.type.replace(/_/g, ' ')}
                          </span>
                          {/* Cross-source badge */}
                          {node.crossSourceCount > 0 && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs bg-purple-50 text-purple-600">
                              <Sparkles className="w-3 h-3" />
                              {node.crossSourceCount}
                            </span>
                          )}
                        </div>
                        {/* Actions */}
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => {
                              setSelectedGraphNode(node.id);
                              setViewMode('graph');
                            }}
                            title="View in graph"
                            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                          >
                            <Network className="w-4 h-4 text-gray-400 hover:text-purple-600" />
                          </button>
                          <button
                            onClick={() => onSearchFromHere(node.label)}
                            title="Search from here"
                            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                          >
                            <ArrowRight className="w-4 h-4 text-gray-400 hover:text-[#2B064F]" />
                          </button>
                          <button
                            onClick={() => toggleExpand(node.id)}
                            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                          >
                            {expanded
                              ? <ChevronDown className="w-4 h-4 text-gray-400" />
                              : <ChevronRight className="w-4 h-4 text-gray-400" />}
                          </button>
                        </div>
                      </div>

                      {/* Label */}
                      <p className="text-sm font-medium text-gray-900 mt-1.5 line-clamp-1">{node.label}</p>

                      {/* Snippet */}
                      {node.content && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{node.content}</p>
                      )}
                    </div>

                    {/* Expanded details */}
                    {expanded && (
                      <div className="px-4 pb-4 pt-3 text-xs text-gray-600 space-y-1 border-t border-gray-100">
                        {node.abstract && <p className="text-gray-500 line-clamp-4">{node.abstract}</p>}
                        {node.timestamp && <p><span className="font-medium">Timestamp:</span> {new Date(node.timestamp).toLocaleString()}</p>}
                        {node.value != null && <p><span className="font-medium">Value:</span> {node.value}</p>}
                        {node.region && <p><span className="font-medium">Region:</span> {node.region}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConnectionsExplorer;
